import React, { useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faPlus } from "@fortawesome/free-solid-svg-icons";
import { CATEGORIAS, seccionOf } from "../ipcFormat";
import { isPlainClick } from "../../crmpostventa-shared/crmLinks";

const OPTIONS = [
  { key: "ipc", title: "Gestión IPC", text: "Modificación, reclamo o consulta del comercio", request: { categoria: CATEGORIAS.IPC, tipo: null } },
  { key: "proactiva", title: "Proactiva", text: "Visita comercial u otra acción del asesor", request: { categoria: CATEGORIAS.PROACTIVA, tipo: null } },
];

// Request of a section: its IPC type preloaded, or a Proactiva; null in Todas, where the advisor chooses.
const requestFor = (seccion) => {
  const current = seccionOf(seccion);
  if (current.categoria) return { categoria: current.categoria, tipo: null };
  if (current.tipo) return { categoria: CATEGORIAS.IPC, tipo: current.tipo };
  return null;
};

/**
 * "Nueva gestión" (design Prompt 1): in Todas it opens a menu with "Gestión IPC" and "Proactiva"; in a section it
 * goes straight to that kind, with the IPC type preloaded. `onSelect` receives { categoria, tipo }.
 * With `hrefFor(request)` the menu options are real links ("Registrar gestión" in the Ficha, Prompt 7): a plain click
 * runs onSelect (the app navigates) and a click with a modifier key opens the link in another tab. `label` and
 * `buttonClassName` replace the text and the style of the button.
 */
export default function NuevaGestionButton({
  seccion,
  disabled = false,
  title,
  onSelect,
  buttonRef,
  label = "Nueva gestión",
  buttonClassName = "crm-rollo__btn crm-rollo__btn--primary",
  hrefFor,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const direct = requestFor(seccion);

  useEffect(() => {
    if (!open) return undefined;
    wrapRef.current?.querySelector('[role="menuitem"]')?.focus();
    const handlePointer = (event) => {
      if (!wrapRef.current?.contains(event.target)) setOpen(false);
    };
    const handleKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  useEffect(() => setOpen(false), [seccion]);

  const choose = (request) => {
    setOpen(false);
    onSelect(request);
  };

  return (
    <div ref={wrapRef} className="crm-ipc__newwrap">
      <button
        ref={buttonRef}
        type="button"
        className={buttonClassName}
        disabled={disabled}
        title={title}
        aria-haspopup={direct ? undefined : "menu"}
        aria-expanded={direct ? undefined : open}
        onClick={() => (direct ? choose(direct) : setOpen((current) => !current))}
      >
        <FontAwesomeIcon icon={faPlus} aria-hidden="true" />
        {label}
        {direct ? null : <FontAwesomeIcon icon={faChevronDown} aria-hidden="true" />}
      </button>
      {open ? (
        <div className="crm-rollo__menu crm-ipc__newmenu" role="menu" aria-label={label}>
          {OPTIONS.map((option) => {
            const content = (
              <span>
                <b>{option.title}</b>
                <span className="crm-rollo__s">{option.text}</span>
              </span>
            );
            const href = hrefFor ? hrefFor(option.request) : null;
            if (href) {
              return (
                <a
                  key={option.key}
                  href={href}
                  role="menuitem"
                  className="crm-rollo__menu-item"
                  onClick={(event) => {
                    setOpen(false);
                    if (!isPlainClick(event)) return;
                    event.preventDefault();
                    onSelect(option.request);
                  }}
                >
                  {content}
                </a>
              );
            }
            return (
              <button key={option.key} type="button" role="menuitem" className="crm-rollo__menu-item" onClick={() => choose(option.request)}>
                {content}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
